import { useState, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { api } from '../../lib/api';
import { useContentManager } from '../../hooks/useContentManager';
import { DownloadCatalogModal } from './FloatingContactIcons';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
  const [logo, setLogo] = useState<string | null>(() => {
    try {
      return localStorage.getItem('site_logo_url');
    } catch {
      return null;
    }
  });
  const [siteTitle, setSiteTitle] = useState('GOLDEN TOWER');
  const [isHidden, setIsHidden] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getContentItem } = useContentManager();
  const [catalogUrl, setCatalogUrl] = useState<string>('/catalog.pdf');

  // Load settings from database
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await api.get('/settings');

        if (data) {
          data.forEach((item) => {
            if (item.setting_key === 'logo_url' && item.setting_value) {
              const logoValue = item.setting_value;
              setLogo(logoValue as string);
              try {
                if (typeof logoValue === 'string') {
                  localStorage.setItem('site_logo_url', logoValue);
                }
              } catch {}
            }
            if (item.setting_key === 'site_title' && item.setting_value) {
              const titleValue = item.setting_value;
              setSiteTitle(titleValue);
            }
            if (item.setting_key === 'pdf_url' && item.setting_value) {
              const pdfValue = item.setting_value;
              if (typeof pdfValue === 'string' && pdfValue.trim()) setCatalogUrl(pdfValue);
            }
          });
        }
      } catch (error) {
        console.error('Settings load error:', error);
      }
    };

    loadSettings();
  }, []);

  // Handle scroll to change background
  useEffect(() => {
    let lastY = window.scrollY;
    const threshold = 10;
    const onScroll = () => {
      const currentY = window.scrollY;
      const isDown = currentY > lastY;
      if (currentY <= 0) {
        setIsHidden(false);
      } else if (isDown && currentY - lastY > threshold) {
        setIsHidden(true);
      } else if (!isDown && lastY - currentY > threshold) {
        setIsHidden(false);
      }
      lastY = currentY;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change and ensure header is visible
  useEffect(() => {
    setIsMenuOpen(false);
    setIsHidden(false);
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // Prefer contact-icons pdf_url over global site_settings
  const contactIcons = getContentItem('home', 'contact-icons');
  const pdfUrl = contactIcons?.metadata?.pdf_url || catalogUrl;

  const handleDownloadCatalog = () => {
    setIsCatalogModalOpen(true);
    setIsMenuOpen(false);
  };

  

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[200] transition-all duration-300 ${
        isHidden ? '-translate-y-full' : 'translate-y-0'
      } pointer-events-none bg-transparent`}>
        <div className="w-full max-w-none mx-auto px-2 sm:px-3 lg:px-4 xl:px-6">
          <div className="flex items-center justify-between py-2 md:py-4">
            {/* Logo with White Glassmorphism Background */}
            <div 
              onClick={() => handleNavigation('/')}
              className="group flex items-center cursor-pointer relative z-[102] pointer-events-auto ml-1 sm:ml-2 lg:ml-3
                         bg-white/90 backdrop-blur-md border border-white/70 rounded-2xl 
                         px-4 py-2 sm:px-5 sm:py-2.5 
                         shadow-[0_10px_35px_rgba(0,0,0,0.3)] 
                         hover:bg-white hover:border-[#C5A46D]/60 hover:shadow-[0_10px_35px_rgba(197,164,109,0.35)] 
                         transition-all duration-300"
            >
              {logo ? (
                <img 
                  src={logo} 
                  alt={siteTitle}
                  className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto object-contain max-w-none group-hover:scale-105 transition-transform duration-300"
                />
              ) : null}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`focus:outline-none cursor-pointer relative z-[102] text-white bg-black/40 backdrop-blur-sm rounded-full p-2 pointer-events-auto mr-4 sm:mr-6 lg:mr-8 xl:mr-10`}
            >
              <div className="w-7 h-7 md:w-6 md:h-6 relative">
                <span className={`absolute block w-full h-0.5 transform transition-all duration-300 bg-white drop-shadow ${
                  isMenuOpen ? 'top-3 rotate-45' : 'top-1'
                }`}></span>
                <span className={`absolute block w-full h-0.5 transform transition-all duration-300 bg-white drop-shadow top-3 ${
                  isMenuOpen ? 'opacity-0' : 'opacity-100'
                }`}></span>
                <span className={`absolute block w-full h-0.5 transform transition-all duration-300 bg-white drop-shadow ${
                  isMenuOpen ? 'top-3 -rotate-45' : 'top-5'
                }`}></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Full Screen Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[101] bg-black overflow-y-auto pt-24 pb-56">
          {/* Menu Content - Centered */}
          <div className="min-h-screen flex items-center justify-center px-6 pb-24 md:pb-40">
            <nav className="flex flex-col items-center space-y-6 md:space-y-8 mb-16 md:mb-24">
              <NavLink
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }: { isActive: boolean }) => `text-2xl md:text-4xl font-light tracking-[0.15em] md:tracking-[0.2em] transition-colors cursor-pointer whitespace-nowrap ${isActive ? 'text-[#C5A46D]' : 'text-white hover:text-[#C5A46D]'}`}
              >
                HOME
              </NavLink>
              <NavLink
                to="/golden-tower"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }: { isActive: boolean }) => `text-2xl md:text-4xl font-light tracking-[0.15em] md:tracking-[0.2em] transition-colors cursor-pointer whitespace-nowrap ${isActive ? 'text-[#C5A46D]' : 'text-white hover:text-[#C5A46D]'}`}
              >
                THE GOLDEN TOWER
              </NavLink>
              <NavLink
                to="/residences"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }: { isActive: boolean }) => `text-2xl md:text-4xl font-light tracking-[0.15em] md:tracking-[0.2em] transition-colors cursor-pointer whitespace-nowrap ${isActive ? 'text-[#C5A46D]' : 'text-white hover:text-[#C5A46D]'}`}
              >
                RESIDENCES
              </NavLink>
              <NavLink
                to="/amenities"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }: { isActive: boolean }) => `text-2xl md:text-4xl font-light tracking-[0.15em] md:tracking-[0.2em] transition-colors cursor-pointer whitespace-nowrap ${isActive ? 'text-[#C5A46D]' : 'text-white hover:text-[#C5A46D]'}`}
              >
                AMENITIES
              </NavLink>
              <NavLink
                to="/blog"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }: { isActive: boolean }) => `text-2xl md:text-4xl font-light tracking-[0.15em] md:tracking-[0.2em] transition-colors cursor-pointer whitespace-nowrap ${isActive ? 'text-[#C5A46D]' : 'text-white hover:text-[#C5A46D]'}`}
              >
                BLOG
              </NavLink>
              <NavLink
                to="/contact"
                onClick={() => setIsMenuOpen(false)}
                className={({ isActive }: { isActive: boolean }) => `text-2xl md:text-4xl font-light tracking-[0.15em] md:tracking-[0.2em] transition-colors cursor-pointer whitespace-nowrap ${isActive ? 'text-[#C5A46D]' : 'text-white hover:text-[#C5A46D]'}`}
              >
                CONTACT
              </NavLink>
              <button
                onClick={handleDownloadCatalog}
                className="text-2xl md:text-4xl font-light tracking-[0.15em] md:tracking-[0.2em] transition-colors cursor-pointer whitespace-nowrap text-white hover:text-[#C5A46D]"
              >
                DOWNLOAD CATALOG
              </button>
            </nav>
          </div>

          {/* Social Media Icons - Bottom */}
          <div className="absolute bottom-12 left-0 right-0 flex justify-center space-x-6">
            {(() => {
              const social = getContentItem('home', 'contact-social');
              const ig = social?.metadata?.instagram || '';
              const fb = social?.metadata?.facebook || '';
              const yt = social?.metadata?.youtube || '';
              const li = social?.metadata?.linkedin || '';
              const x = social?.metadata?.snapchat || '';
              return (
                <>
                  {ig && ig !== '#' && (
                    <a href={ig} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-[#C5A46D] hover:text-[#C5A46D] transition-all duration-300">
                      <i className="ri-instagram-line text-lg"></i>
                    </a>
                  )}
                  {fb && fb !== '#' && (
                    <a href={fb} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-[#C5A46D] hover:text-[#C5A46D] transition-all duration-300">
                      <i className="ri-facebook-fill text-lg"></i>
                    </a>
                  )}
                  {yt && yt !== '#' && (
                    <a href={yt} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-[#C5A46D] hover:text-[#C5A46D] transition-all duration-300">
                      <i className="ri-youtube-fill text-lg"></i>
                    </a>
                  )}
                  {li && li !== '#' && (
                    <a href={li} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-[#C5A46D] hover:text-[#C5A46D] transition-all duration-300">
                      <i className="ri-linkedin-fill text-lg"></i>
                    </a>
                  )}
                  {x && x !== '#' && (
                    <a href={x} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-[#C5A46D] hover:text-[#C5A46D] transition-all duration-300">
                      <i className="ri-twitter-fill text-lg"></i>
                    </a>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
      
      {/* Download Catalog Modal */}
      <DownloadCatalogModal
        isOpen={isCatalogModalOpen}
        onClose={() => setIsCatalogModalOpen(false)}
        pdfUrl={pdfUrl || '/catalog.pdf'}
      />
    </>
  );
}
